import { Fragment, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Card, PageHeader, Button, Checkbox, Input, showSnackbar } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import CurrencySelect from "components/selects/CurrencySelect";
import Helmet from "components/helmet/Helmet";
import EmailNotificationTypeSelect from "components/selects/EmailNotificationTypeSelect";

import FleetSelect from "view/fleets/components/selects/FleetSelect";

import { Language, Fields, hasOptionalFieldPermission, usePermission } from "imports/Utils";

import { GetAccountByIdDocument } from "__generated__/view/users/graphql/user.queries.generated";
import { UpdateAccountDocument } from "__generated__/view/users/graphql/user.mutations.generated";

import { userEditSchema, UserEditSchemaInput, UserEditSchemaOutput } from "./utils/user.schema";
import { AccountType } from "__generated__/enums";

const UserEdit = () => {

	const session = useStoreState(store => store.session);
	const navigate = useNavigate();
	const { id } = useParams();

	const [ apiPasswordSaved, setApiPasswordSaved ] = useState(false);

	const { handleSubmit, control, reset, watch } = useForm<UserEditSchemaInput, unknown, UserEditSchemaOutput>({
		resolver: zodResolver(userEditSchema),
		defaultValues: {
			userEmailNotificationType: null,
			preferredCurrency: null,
			fleets: null,
			allowSystemInput: false,
			apiPassword: "",
		},
	});

	const { isAdmin } = usePermission();

	const { data } = useQuery(GetAccountByIdDocument, {
		variables: {
			id: Number(id),
		},
		onCompleted: data => {
			const account = data.getAccountById;
			setApiPasswordSaved(account.allowSystemInput);
			reset({
				userEmailNotificationType: account.userEmailNotificationType ?? null,
				preferredCurrency: account.preferredCurrency ?? null,
				fleets: account.fleets ?? null,
				allowSystemInput: account.allowSystemInput ?? false,
				apiPassword: "",
			});
		},
	});

	const user = data?.getAccountById;
	const showApiPasswordField = !apiPasswordSaved && watch("allowSystemInput");

	const [ updateAccount, { loading } ] = useMutation(UpdateAccountDocument);

	const saveUser = (data: UserEditSchemaOutput) => {
		updateAccount({
			variables: {
				accountId: user!.id,
				input: {
					preferredCurrency: data.preferredCurrency?.isoCode ? {
						isoCode: data.preferredCurrency.isoCode,
					} : null,
					allowSystemInput: data.allowSystemInput,
					apiPassword: data.apiPassword || null,
					fleets: data.fleets?.map((fleet) => ({ id: fleet.id })) ?? [],
					relation: user?.relation ? { id: user.relation.id } : null,
					userEmailNotificationType: data.userEmailNotificationType,
				},
			},
		}).then((result) => {
			if (result.data) {
				showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
				navigate(`/view/users/${result.data.updateAccount.id}/details`);
			}
		});
	};

	return (
		<form onSubmit={handleSubmit(saveUser)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("USER")}
					<Helmet title={user?.username || user?.displayName || intl.formatMessage({ id: "USER" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button
						onClick={() => navigate(`/view/users/${user?.id}/details`)}
						loading={loading}
						variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button onClick={handleSubmit(saveUser)} loading={loading}>
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card>
						<FormGroup label={Language("USER_TYPE")} labelPlacement="left-center" overflow="hidden">
							{user?.accountType || "-"}
						</FormGroup>

						<FormGroup label={Language("USER_USERNAME")} labelPlacement="left-center" overflow="hidden">
							{user?.username || "-"}
						</FormGroup>

						<FormGroup label={Language("USER_FULLNAME")} labelPlacement="left-center" overflow="hidden">
							{user?.displayName || "-"}
						</FormGroup>

						{user?.accountType === AccountType.Operator && (
							<Fragment>
								<FormGroup label={Language("ADD_USER_SHIPS")} labelPlacement="left-center" overflow="hidden">
									{user.ship?.name || "-"}
								</FormGroup>

								<FormGroup label={Language("OPERATOR")} labelPlacement="left-center" overflow="hidden">
									{user.relation?.name}
								</FormGroup>
							</Fragment>)}

						{user?.accountType === AccountType.Terminal && (
							<FormGroup label={Language("LOCATION_MANAGER")} labelPlacement="left-center" overflow="hidden">
								{user.relation?.name || "-"}
							</FormGroup>
						)}

						{user?.accountType === AccountType.Customer && (
							<FormGroup label={Language("CLIENT_SINGLE")} labelPlacement="left-center" overflow="hidden">
								{user.relation?.name || "-"}
							</FormGroup>
						)}

						<FormGroup label={Language("USER_EMAIL")} labelPlacement="left-center" overflow="hidden">
							{user?.email || "-"}
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.USER, "secondEmail") && (
							<FormGroup label={Language("USER_SECONDARY_EMAIL")} labelPlacement="left-center" overflow="hidden">
								{user?.secondEmail || "-"}
							</FormGroup>
						)}

						{(session.user?.properties.id === user?.id || isAdmin) &&
							<FormGroup label={Language("SECONDARY_EMAIL_TYPE")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="userEmailNotificationType"
									control={control}
									render={({ field }) => <EmailNotificationTypeSelect {...field} />} />
							</FormGroup>
						}

						{hasOptionalFieldPermission(session, Fields.USER, "phone") && (
							<FormGroup label={Language("USER_PHONE")} labelPlacement="left-center" overflow="hidden">
								{user?.phone || "-"}
							</FormGroup>
						)}

						{hasOptionalFieldPermission(session, Fields.USER, "mobilePhone") && (
							<FormGroup label={Language("USER_MOBILE")} labelPlacement="left-center" overflow="hidden">
								{user?.mobilePhone || "-"}
							</FormGroup>
						)}

						<FormGroup label={Language("USER_LANGUAGE")} labelPlacement="left-center" overflow="hidden">
							{user?.language?.description || "-"}
						</FormGroup>

						<FormGroup label={Language("PREFERRED_CURRENCY")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="preferredCurrency"
								control={control}
								render={({ field }) => <CurrencySelect {...field} />} />
						</FormGroup>

						<FormGroup label={Language("USER_FLEETS")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="fleets"
								control={control}
								render={({ field }) => <FleetSelect {...field} multiple />} />
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.USER, "allowSystemInput") && (
							<Fragment>
								<FormGroup label={Language("USER_ALLOW_SYSTEM_INPUT")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="allowSystemInput"
										control={control}
										render={({ field }) => <Checkbox {...field} checked={field.value} value={undefined} />} />
								</FormGroup>

								{showApiPasswordField && (
									<FormGroup label="BOS API Password" labelPlacement="left-center" overflow="hidden">
										<Controller
											name="apiPassword"
											control={control}
											render={({ field }) => <Input type="password" {...field} />} />
									</FormGroup>
								)}
							</Fragment>
						)}

						{hasOptionalFieldPermission(session, Fields.USER, "batchMessages") && (
							<FormGroup label={Language("USER_NOTIFICATION_SETTINGS")} labelPlacement="left-center" overflow="hidden">
								{user?.batchMessages || "-"}
							</FormGroup>
						)}
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default UserEdit;
